import React from 'react';
import { Avatar, Select, Tag } from 'antd';

import TodoLabelTag from './TodoLabelTag';

/**
 * The label and member pickers, defined once and used by BOTH the task form and the task
 * drawer.
 *
 * They had drifted apart: the form rendered each option as a coloured Tag while the
 * drawer rendered plain text, so the same field looked like two different controls
 * depending on where you opened it. Sharing the component is the only way that stays
 * fixed — matching them by hand lasts until the next edit to one of them.
 */

const initials = (name = '') => name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

/**
 * Multi-select for labels.
 *
 * `tagRender` is the point. In multiple mode Ant wraps every selected value in its own
 * grey tag; putting a coloured Tag inside an Option therefore produced a coloured pill
 * nested inside a grey one. Rendering the selected value AS the coloured tag replaces
 * that wrapper instead of sitting inside it.
 */
export const TodoLabelSelect = ({ value, onChange, labels = [], size, loading, disabled, style }) => {
    const options = labels.map((l) => ({
        value: l.id,
        label: l.name,
        color: l.color || 'default',
    }));

    return (
        <Select
            mode="multiple"
            size={size}
            loading={loading}
            disabled={disabled}
            value={value}
            onChange={onChange}
            placeholder="No labels"
            allowClear
            optionFilterProp="label"
            style={{ width: '100%', ...style }}
            options={options}
            optionRender={(option) => (
                <TodoLabelTag color={option.data.color} style={{ marginInlineEnd: 0 }}>{option.data.label}</TodoLabelTag>
            )}
            tagRender={({ value: v, closable, onClose }) => {
                const option = options.find((o) => o.value === v);

                return (
                    <TodoLabelTag
                        color={option?.color || 'default'}
                        closable={closable}
                        // Without this the dropdown toggles when the close icon is used.
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onClose={onClose}
                        style={{ marginInlineEnd: 4 }}
                    >
                        {option?.label ?? v}
                    </TodoLabelTag>
                );
            }}
        />
    );
};

/** Multi-select for members, with the same avatar treatment the cards use. */
export const TodoMemberSelect = ({ value, onChange, assignees = [], size, loading, disabled, style }) => {
    const options = assignees.map((u) => ({
        value: u.id,
        label: u.is_me ? `${u.name} (me)` : u.name,
        name: u.name,
    }));

    return (
        <Select
            mode="multiple"
            size={size}
            loading={loading}
            disabled={disabled}
            value={value}
            onChange={onChange}
            placeholder="Unassigned"
            allowClear
            optionFilterProp="label"
            style={{ width: '100%', ...style }}
            options={options}
            optionRender={(option) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Avatar size={20} style={{ fontSize: 9, background: 'var(--ant-color-primary)' }}>
                        {initials(option.data.name)}
                    </Avatar>
                    {option.data.label}
                </span>
            )}
            tagRender={({ value: v, closable, onClose }) => {
                const option = options.find((o) => o.value === v);

                return (
                    <Tag
                        closable={closable}
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onClose={onClose}
                        style={{ marginInlineEnd: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                        <Avatar size={16} style={{ fontSize: 8, background: 'var(--ant-color-primary)' }}>
                            {initials(option?.name || '')}
                        </Avatar>
                        {option?.label ?? v}
                    </Tag>
                );
            }}
        />
    );
};
